import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Sparkles, ArrowRight } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Reveal, FireButton, GhostButton, Aurora, Logo } from "@/components/fx";
import { LegalLinks } from "@/pages/Legal";
import { billingApi } from "@/lib/api";

const TABS = ["Brief", "System Map", "Build Console", "Copilot", "Connect"];

const Screen = ({ tab }) => {
  const rows = {
    Brief: ["App · Fade & Co. Booking", "Users · walk-ins, regulars, barbers", "Rule · a barber can’t be double-booked", "Success · 90% of bookings in < 60s"],
    Copilot: ["you › add a waitlist when a slot is full", "forge › plan: +1 collection, +1 screen, +1 rule", "forge › rebuilding 3 files · re-running 44 checks", "forge › READY · v4 published"],
    Connect: ["SMS · reminder 2h before appointment", "Voice · after-hours receptionist line", "Email · weekly summary for the owner", "Video · consult room for premium clients"],
  }[tab];
  if (tab === "System Map") return (
    <svg viewBox="0 0 420 220" className="h-full w-full">
      {[[60, 60, 210, 110], [60, 160, 210, 110], [210, 110, 360, 60], [210, 110, 360, 160]].map(([a, b, c, d], i) => (
        <motion.path key={i} d={`M${a},${b} C${(a + c) / 2},${b} ${(a + c) / 2},${d} ${c},${d}`} stroke="url(#pg)" strokeWidth="2" fill="none" initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ delay: i * 0.2, duration: 0.8 }} />
      ))}
      <defs><linearGradient id="pg"><stop offset="0" stopColor="#22d3ee" /><stop offset="1" stopColor="#ff5c7a" /></linearGradient></defs>
      {[[60, 60, "Booking UI"], [60, 160, "Owner view"], [210, 110, "Rules engine"], [360, 60, "bookings"], [360, 160, "barbers"]].map(([x, y, l], i) => (
        <motion.g key={l} initial={{ opacity: 0, scale: 0.6 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: i * 0.12 }}>
          <rect x={x - 48} y={y - 15} width="96" height="30" rx="9" fill="#13101f" stroke="#7a5cff" />
          <text x={x} y={y + 4} textAnchor="middle" fontSize="11" fill="#fff" fontFamily="Inter">{l}</text>
        </motion.g>
      ))}
    </svg>
  );
  if (tab === "Build Console") return (
    <div className="grid h-full grid-cols-3 gap-3">
      {[["QA", "41/41"], ["Security", "11/12"], ["Repairs", "1"]].map(([k, v], i) => (
        <motion.div key={k} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.12 }} className="rounded-2xl border border-white/10 bg-white/[0.04] p-4">
          <div className="label-mono text-white/40">{k}</div><div className="font-display mt-2 text-3xl font-bold text-white">{v}</div>
          <div className="beam mt-4 h-1.5 rounded-full bg-gradient-to-r from-[#7a5cff] to-[#22d3ee]" />
        </motion.div>
      ))}
    </div>
  );
  return (
    <div className="space-y-2">
      {rows.map((r, i) => (
        <motion.div key={r} initial={{ opacity: 0, x: -14 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.15 }} className="rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3 font-mono text-sm text-white/80">{r}</motion.div>
      ))}
    </div>
  );
};

export const ProductPreview = () => {
  const [tab, setTab] = useState(TABS[0]);
  return (
    <section data-testid="product-preview-section" className="mx-auto max-w-6xl px-6 py-16 md:py-20">
      <Reveal><h2 className="font-display text-center text-3xl font-bold text-white md:text-5xl">A command center for everything you build.</h2></Reveal>
      <Reveal delay={0.1}>
        <div className="glass-strong noise relative mt-12 overflow-hidden rounded-3xl">
          <div className="flex flex-wrap gap-1 border-b border-white/10 p-2">
            {TABS.map((t) => (
              <button key={t} data-testid={`preview-tab-${t.toLowerCase().replace(" ", "-")}`} onClick={() => setTab(t)} className={`relative rounded-xl px-4 py-2 text-sm font-medium transition-colors ${tab === t ? "text-white" : "text-white/50 hover:text-white/80"}`}>
                {tab === t && <motion.span layoutId="ptab" className="absolute inset-0 rounded-xl bg-white/10" />}
                <span className="relative">{t}</span>
              </button>
            ))}
          </div>
          <div className="h-[300px] p-6"><AnimatePresence mode="wait"><motion.div key={tab} className="h-full" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}><Screen tab={tab} /></motion.div></AnimatePresence></div>
        </div>
      </Reveal>
    </section>
  );
};

export const Pricing = () => {
  const [data, setData] = useState(null);
  const [yearly, setYearly] = useState(false);
  useEffect(() => { billingApi.plans().then(setData).catch(() => {}); }, []);
  return (
    <section id="pricing" data-testid="pricing-section" className="mx-auto max-w-7xl px-6 py-16 md:py-20">
      <Reveal><h2 className="font-display text-center text-3xl font-bold text-white md:text-5xl">Fair, transparent pricing.</h2></Reveal>
      <p className="mt-3 text-center text-base text-white/70">Real, working, database-backed apps from day one — tested with evidence.</p>
      <p className="mt-2 text-center text-sm text-white/50">See the cost before every build. Usage beyond included credit at provider cost +30%. Wallet top-ups never expire.</p>
      <label className="mt-5 flex items-center justify-center gap-2 text-sm text-white/60"><input data-testid="pricing-yearly-toggle" type="checkbox" checked={yearly} onChange={(e) => setYearly(e.target.checked)} />Annual billing (save 20%)</label>
      <div className="mt-10 grid gap-4 md:grid-cols-3 lg:grid-cols-5">
        {(data?.plans || []).map((p, i) => {
          const life = p.key === "lifetime";
          const price = life && p.founders_active ? p.founders_price : yearly && p.price_yearly_monthly ? p.price_yearly_monthly : p.price;
          return (
            <Reveal key={p.key} delay={i * 0.06}>
              <motion.div whileHover={{ y: -6 }} data-testid={`pricing-${p.key}`} className={`relative flex h-full flex-col rounded-3xl p-6 ${life ? "glow-coral border border-[#ff5c7a]/50 bg-gradient-to-b from-[#ff5c7a]/15 to-[#7a5cff]/10" : "glass"} ${p.popular ? "ring-1 ring-[#7a5cff]/60" : ""}`}>
                {life && <span className="label-mono absolute -top-3 left-6 rounded-full bg-fire px-3 py-1 text-[10px] text-white"><Sparkles className="mr-1 inline h-3 w-3" />Founders</span>}
                {p.popular && <span className="label-mono absolute -top-3 left-6 rounded-full bg-[#7a5cff] px-3 py-1 text-[10px] text-white">Most popular</span>}
                <div className="font-display text-lg font-semibold text-white">{p.label}</div>
                <div className="mt-3"><span className="font-display text-3xl font-bold text-white">${price % 1 ? price.toFixed(2) : price}</span> <span className="text-sm text-white/50">{p.interval ? "/mo" : life ? "one-time" : ""}</span>{life && p.founders_active && <span className="ml-2 text-sm text-white/40 line-through">${p.price}</span>}</div>
                {life && p.founders_active && <div className="label-mono text-[10px] text-[#ff9aac]">Founders price · {p.founders_spots - p.founders_taken} left</div>}
                <div className="mt-1 text-xs text-white/45">{p.ai_credit ? `$${p.ai_credit} AI credit/mo` : "Fast models"}{p.builds_included_estimate ? ` · ≈${p.builds_included_estimate} builds` : ""}</div>
                <ul className="mt-5 flex-1 space-y-2">{p.features.map((x) => <li key={x} className="flex gap-2 text-sm text-white/70"><Check className="mt-0.5 h-4 w-4 shrink-0 text-[#6fd6b0]" />{x}</li>)}</ul>
                {life && <div className="mt-4 label-mono text-[10px] text-white/50">{p.spots_total - p.spots_taken} of {p.spots_total} founder spots left</div>}
                <Link to={p.key === "free" ? "/signup" : `/app/billing?plan=${p.key}${yearly && p.interval ? "&yearly=1" : ""}`} data-testid={`pricing-cta-${p.key}`} className={`mt-6 rounded-full py-2.5 text-center text-sm font-semibold transition-colors ${life || p.popular ? "bg-fire text-white" : "border border-white/15 text-white hover:bg-white/10"}`}>{p.key === "free" ? "Start free" : "Get started"}</Link>
              </motion.div>
            </Reveal>
          );
        })}
      </div>
      {data?.avg_build_cost && <p className="mt-4 text-center text-[11px] text-white/40">≈ builds estimate: {data.avg_build_cost.basis} (${data.avg_build_cost.usd} per build).</p>}
    </section>
  );
};

const FAQS = [
  ["Is the generated app real or a mockup?", "Real. Every app gets its own data API with server-side rules, and FORGE runs CRUD smoke tests and rule probes against it before calling it READY."],
  ["Which AI models power FORGE?", "Auto mode (default) routes each step to the right model: fast ones (Claude Haiku 4.5, Gemini 3 Flash), strong ones (GPT 5.5, Claude Sonnet 5.5, Gemini 3.1 Pro) and, on Business and Lifetime, top-tier Claude Opus 5.5 and GPT 6 Luna. Or pick one yourself."],
  ["What happens when a check fails?", "The Repair Agent receives the exact error and the file, applies a fix and the checks re-run — up to three loops. If it still fails, FORGE tells you exactly what needs you."],
  ["Can I edit the plan before building?", "Yes. Every brief item and plan step is editable. Nothing is built until you approve."],
  ["Can I export the code?", "Code export is included with the Lifetime plan. You own the apps you build on every plan."],
  ["Does FORGE ever show a fake green check?", "No. Every check is PASSED, FAILED, SKIPPED, BLOCKED or UNVERIFIED, with screenshots and request/response evidence. If a test couldn't run, it says so."],
  ["What happens when I ask for a change to a live app?", "FORGE shows you the impact first, builds a draft on a sandbox copy, runs the app's acceptance and regression tests, and only then lets you publish or discard. Every version can be rolled back."],
  ["Can my apps text, call, email or video-call customers?", "Yes, through FORGE Connect: SMS, MMS, voice, video rooms, email and campaigns with consent and STOP handling. Delivery depends on the provider being connected; if it isn't, messages are marked Not sent — never faked."],
  ["Will I know what a build costs?", "Yes. You see an estimate before you approve a build and the actual model and messaging cost afterwards."],
];

export const Faq = () => (
  <section data-testid="faq-section" className="mx-auto max-w-3xl px-6 py-16 md:py-20">
    <Reveal><h2 className="font-display text-center text-3xl font-bold text-white md:text-5xl">Questions, answered.</h2></Reveal>
    <Accordion type="single" collapsible className="mt-10">
      {FAQS.map(([q, a], i) => (
        <AccordionItem key={q} value={`f${i}`} className="border-white/10">
          <AccordionTrigger data-testid={`faq-q-${i}`} className="text-left font-display text-white hover:no-underline">{q}</AccordionTrigger>
          <AccordionContent className="text-white/60">{a}</AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  </section>
);

export const FinalCta = () => (
  <section className="relative isolate mx-auto my-12 max-w-6xl overflow-hidden rounded-[2.5rem] px-6 py-24 text-center">
    <div className="absolute inset-0 -z-10 bg-gradient-to-br from-[#2a1a6e] via-[#13101f] to-[#5a1830]" />
    <Aurora className="-z-10" />
    <Reveal><h2 className="font-display mx-auto max-w-3xl text-4xl font-bold text-white md:text-6xl">Your next product is one sentence away.</h2></Reveal>
    <div className="mt-10 flex justify-center gap-3">
      <FireButton as={Link} to="/signup" data-testid="final-cta-start">Start building <ArrowRight className="h-4 w-4" /></FireButton>
      <GhostButton as={Link} to="/login" data-testid="final-cta-login">Log in</GhostButton>
    </div>
  </section>
);

export const Footer = () => (
  <footer className="border-t border-white/10 px-6 py-10">
    <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-6 text-sm text-white/45">
      <Logo size={26} />
      <span>The AI operating system for building software.</span>
      <span className="ml-auto">© {new Date().getFullYear()} NetherDev · FORGE · forgeaibuilder.app</span>
    </div>
    <LegalLinks />
  </footer>
);
